import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { differenceInCalendarDays, format } from "date-fns";
import { TbShoppingCartPlus } from "react-icons/tb";
import { IoChevronDown } from "react-icons/io5";
import EAModal from "../../shared/Modal/EAModal";
import EAButton from "../../shared/Buttons/EAButton";
import EAAlert from "../../shared/EAAlert";
import EASkeletonLoader from "../../shared/EASkeletonLoader";
import { AssortGroup } from "../../../models/AssortGroup";
import useFacility from "../../../../utils/hooks/useFacility";
import useClubCart from "../ClubPassRules/hook/useClubCart";
import usePassDateLimits from "./usePassDateLimits";
import PassDateBars from "./PassDateBars";
import ClubPassNotices from "./ClubPassNotices";
import "./style.scss";

const CLUB_PASS_TYPE = 8;
const DESKTOP_LAYOUT_QUERY = "(min-width: 981px)";
const DESCRIPTION_PREVIEW_LINES = 3;

const isDesktopLayout = () =>
  typeof window !== "undefined" && window.matchMedia(DESKTOP_LAYOUT_QUERY).matches;

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  pass: AssortGroup;
  selectedDate: Date;
  setDateBetween: (date: Date) => void;
  onAddToBasket: () => void;
  errorMessage?: string;
}

const PassModal: React.FC<IProps> = ({ isOpen, onClose, pass, selectedDate, setDateBetween, onAddToBasket, errorMessage }) => {
  const { t } = useTranslation();
  const facilityIds = useFacility();
  const { minDate, maxDate, getEndDate } = usePassDateLimits(pass.dayValidate);
  const { getClubCartDetails, clubCarnetDetails, loading: clubCartLoading } = useClubCart();

  const [clubCartAgreement, setClubCartAgreement] = useState(false);
  const [continuityAgreement, setContinuityAgreement] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(isDesktopLayout);
  const [isWideLayout, setIsWideLayout] = useState(isDesktopLayout);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isDescriptionMotionEnabled, setIsDescriptionMotionEnabled] = useState(false);
  const [descriptionFullHeight, setDescriptionFullHeight] = useState(0);
  const [descriptionCollapsedHeight, setDescriptionCollapsedHeight] = useState(0);

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const isClubPass = pass.passtype === CLUB_PASS_TYPE;
  const passName = pass.displayWebName || pass.name;
  const hasDescription = (pass.description?.length ?? 0) > 6;
  const isDiscount = pass.listPrice > pass.price;

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_LAYOUT_QUERY);
    const onChange = () => setIsWideLayout(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (isClubPass && isOpen) {
      getClubCartDetails(`0,${pass.objectId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClubPass, pass.objectId, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setDateBetween(new Date());
      setIsDescriptionMotionEnabled(false);
      setIsDescriptionExpanded(isDesktopLayout());
      return;
    }
    setClubCartAgreement(false);
    setContinuityAgreement(false);
    setIsDescriptionMotionEnabled(false);
    setIsDescriptionExpanded(false);
    setIsCalendarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useLayoutEffect(() => {
    const description = descriptionRef.current;
    if (!isOpen || !description) return;

    const measure = () => {
      const probe = description.firstElementChild instanceof HTMLElement ? description.firstElementChild : description;
      const styles = window.getComputedStyle(probe);
      const fontSize = parseFloat(styles.fontSize) || 16;
      const parsedLineHeight = parseFloat(styles.lineHeight);
      const lineHeight = Number.isNaN(parsedLineHeight) ? fontSize * 1.3 : parsedLineHeight;
      setDescriptionCollapsedHeight(Math.ceil(lineHeight * DESCRIPTION_PREVIEW_LINES));
      setDescriptionFullHeight(description.scrollHeight);
    };
    measure();

    const resizeObserverCtor = (window as unknown as Record<string, unknown>).ResizeObserver as
      | (new (callback: () => void) => { observe: (target: Element) => void; disconnect: () => void })
      | undefined;

    if (!resizeObserverCtor) {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new resizeObserverCtor(measure);
    observer.observe(description);
    return () => observer.disconnect();
  }, [isOpen, pass.description]);

  const descriptionPreviewHeight = isWideLayout ? descriptionCollapsedHeight : 0;
  const isDescriptionOverflowing = descriptionFullHeight > descriptionPreviewHeight + 1;
  const descriptionHeight = !isWideLayout && !isDescriptionExpanded
    ? 0
    : descriptionFullHeight === 0
      ? undefined
      : isDescriptionExpanded || !isDescriptionOverflowing
        ? descriptionFullHeight
        : descriptionPreviewHeight;

  const handleToggleDescription = () => {
    setIsDescriptionMotionEnabled(true);
    if (isDescriptionExpanded) {
      scrollAreaRef.current?.scrollTo({ top: 0 });
    }
    setIsDescriptionExpanded((expanded) => !expanded);
  };

  const isRenewal = isClubPass && clubCarnetDetails?.first === false;
  const hasRenewalRange = isRenewal && Boolean(clubCarnetDetails?.from && clubCarnetDetails?.to);
  const startDate = hasRenewalRange ? new Date(clubCarnetDetails!.from) : selectedDate;
  const endDate = hasRenewalRange ? new Date(clubCarnetDetails!.to) : getEndDate(selectedDate);

  const hasContinuityIssue = isRenewal && !clubCartLoading && (clubCarnetDetails?.daysBetween ?? 0) > 0;
  const isAddDisabled = isClubPass && (clubCartLoading || !clubCartAgreement || (hasContinuityIssue && !continuityAgreement));

  return (
    <EAModal isOpen={isOpen} title="" onClose={onClose} hideButtons className="pass-modal">
      <div
        ref={scrollAreaRef}
        className={`pass-modal-container${isDescriptionExpanded ? " pass-modal-container--expanded" : ""}`}
      >
        <div className="pass-modal-product-info">
          {pass.picture && <img alt={passName} src={pass.picture} />}

          <h4 className="pass-modal-product-info__name">{passName}</h4>

          {hasDescription && (
            <>
              <div
                className={`pass-modal-product-info__description-clip${
                  isDescriptionMotionEnabled ? " is-animated" : ""
                }${isWideLayout && isDescriptionOverflowing && !isDescriptionExpanded ? " is-faded" : ""}`}
                style={descriptionHeight === undefined ? undefined : { height: descriptionHeight }}
                aria-hidden={!isWideLayout && !isDescriptionExpanded}
              >
                <div
                  ref={descriptionRef}
                  className="pass-modal-product-info__description"
                  dangerouslySetInnerHTML={{ __html: pass.description }}
                />
              </div>
              {(isDescriptionOverflowing || isDescriptionExpanded || !isWideLayout) && (
                <div className={`pass-modal-product-info__toggle${isDescriptionExpanded ? " is-expanded" : ""}`}>
                  <EAButton
                    variant="text"
                    color="dark"
                    compact
                    noMargins
                    title={t(isDescriptionExpanded ? "pokaz_mniej" : "pokaz_wiecej")}
                    endIcon={<IoChevronDown size={16} aria-hidden="true" />}
                    onClick={handleToggleDescription}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className={`pass-modal-date-choice${isCalendarOpen ? " pass-modal-date-choice--calendar-open" : ""}`}>
          <div className="pass-modal-date-choice__content">
            {isClubPass && clubCartLoading ? (
              <EASkeletonLoader height="140px" />
            ) : (
              <PassDateBars
                startDate={startDate}
                endDate={endDate}
                minDate={minDate}
                maxDate={maxDate}
                canChangeDate={!hasRenewalRange}
                onStartDateChange={setDateBetween}
                onCalendarOpenChange={setIsCalendarOpen}
              />
            )}

            {isClubPass && !clubCartLoading && (
              <ClubPassNotices
                facilityIds={facilityIds}
                clubCarnetDetails={clubCarnetDetails}
                rulesAccepted={clubCartAgreement}
                onRulesAcceptedChange={setClubCartAgreement}
                hasContinuityIssue={hasContinuityIssue}
                continuityAccepted={continuityAgreement}
                onContinuityAcceptedChange={setContinuityAgreement}
              />
            )}
          </div>

          <div className="pass-modal-footer">
            {errorMessage && <EAAlert type="error" message={errorMessage} compact />}
            <div className="pass-modal-footer__lines">
              <div className="flex-between-center">
                <span className="body-text-small text-color-dark-gray">{t("waznosc")}</span>
                <span className="body-text-small text-bold">
                  {format(startDate, "dd.MM.yyyy")} – {format(endDate, "dd.MM.yyyy")}
                </span>
              </div>
              <div className="flex-between-center">
                <span className="body-text-small text-color-dark-gray">{t("okres")}</span>
                <span className="body-text-small text-bold">
                  {Math.max(differenceInCalendarDays(endDate, startDate), 0)} {t("dni")}
                </span>
              </div>
            </div>
            <div className="pass-modal-footer__row">
              <div className="pass-modal-footer__price">
                <span className="caption text-color-dark-gray">{t("cena")}</span>
                <div className="pass-modal-footer__price-row">
                  <h4>
                    {pass.price.toFixed(2)} {t("zl")}
                  </h4>
                  {isDiscount && (
                    <span className="body-text-small text-color-dark-gray pass-modal-footer__price-crossed">
                      {pass.listPrice.toFixed(2)} {t("zl")}
                    </span>
                  )}
                </div>
              </div>
              <EAButton
                title={t("dodaj_do_koszyka")}
                endIcon={<TbShoppingCartPlus />}
                onClick={onAddToBasket}
                disabled={isAddDisabled}
                animationType="addToBasket"
              />
            </div>
          </div>
        </div>
      </div>
    </EAModal>
  );
};

export default PassModal;
